import { apiClient } from '@/services/api/apiClient';
import { ENDPOINTS } from '@/services/api/endpoints';
import {
    normalizeInt1To100,
    DEFAULT_WEIGHT,
    DEFAULT_TARGET_VALUE,
    mapApiTaskToChairUi,
    mapProjectForDashboard,
    mapProjectRowForManagement,
    toIsoDateEnd,
    toIsoDateStart,
    toCheckpointPayload,
} from '@/features/pillarChair/services/pillarChairService';

function normalizeProjectListPayload(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.projects)) return data.projects;
    if (Array.isArray(data?.data)) return data.data;
    return [];
}

function normalizeTaskListPayload(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.tasks)) return data.tasks;
    if (Array.isArray(data?.data)) return data.data;
    return [];
}

function displayNameFromUser(u) {
    if (!u) return '';
    const parts = [u.firstName, u.middleName, u.lastName].filter(Boolean);
    const s = parts.join(' ').trim();
    return s || u.email || u.id;
}

function primaryRoleLabel(u) {
    const ar = u?.account?.accountRoles;
    if (!Array.isArray(ar) || ar.length === 0) return 'Member';
    const name = ar[0]?.role?.name;
    return name || 'Member';
}

function normalizeMyProjectsPayload(body) {
    if (body == null) return [];
    const inner = body.data != null ? body.data : body;
    return Array.isArray(inner) ? inner : [];
}

function normalizeCheckpointListPayload(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.checkpoints)) return data.checkpoints;
    if (Array.isArray(data?.data)) return data.data;
    return [];
}

export const projectManagerService = {
    async getMyProjects() {
        const { data } = await apiClient.get(ENDPOINTS.PILLARS.MY_PROJECTS);
        return normalizeMyProjectsPayload(data);
    },

    /**
     * Returns this service pinned to an explicit project list instead of the
     * caller's assigned ones.
     *
     * `resolveScope`, `listTasksFlat` and `listAllProjectsForManagement` all read
     * their universe from `getMyProjects()`, so overriding that single method on a
     * prototype-linked copy re-scopes the service. Used by the admin Task
     * Management page, which works across projects not assigned to the admin.
     *
     * @param {Array<{ id: string, taskForceId: string, pillarId: string }>} projects
     */
    forProjects(projects) {
        const list = Array.isArray(projects) ? projects : [];
        if (!list.length) return this;
        return Object.create(this, {
            getMyProjects: { value: async () => list },
        });
    },

    /**
     * @param {string} [projectId] — when set, must be one of the PM’s assigned projects; otherwise first project.
     */
    async resolveScope(projectId) {
        const list = await this.getMyProjects();
        if (!list.length) {
            throw new Error('No project is assigned to your account.');
        }
        const p = projectId ? list.find((x) => x.id === projectId) : list[0];
        if (!p?.taskForceId || !p?.pillarId || !p?.id) {
            throw new Error(
                projectId ? 'Project not found or not assigned to you.' : 'No project is assigned to your account.',
            );
        }
        return {
            taskForceId: p.taskForceId,
            pillarId: p.pillarId,
            projectId: p.id,
            projectName: p.name ?? '',
        };
    },

    async loadProjectManagerDashboard() {
        const scope = await this.resolveScope(undefined);
        // A project-fetch failure must not erase an already-resolved scope
        // (the dashboard treats a null scope as "no project assigned", which would be
        // wrong for an account that does have one).
        try {
            const { data } = await apiClient.get(
                ENDPOINTS.HIERARCHY.PROJECTS.LIST(scope.taskForceId, scope.pillarId),
            );
            const raw = normalizeProjectListPayload(data);
            const mine = raw.filter((pr) => pr.id === scope.projectId);
            return {
                scope,
                projects: mine.map(mapProjectForDashboard),
            };
        } catch {
            return { scope, projects: [] };
        }
    },

    /** `params` optionally carries the dashboard date range (`startDate`/`endDate`). */
    async getDashboardSummary(params = {}) {
        const { data } = await apiClient.get(ENDPOINTS.DASHBOARD.PROJECT_MANAGER_SUMMARY, {
            params,
        });
        return data ?? null;
    },

    /**
     * Project header, task board, real weekly velocity, approvals queue and
     * level-1 escalations — the work a Project Manager actually does day to day.
     */
    async getWorkContext(params = {}) {
        const { data } = await apiClient.get(ENDPOINTS.DASHBOARD.PROJECT_MANAGER_WORK_CONTEXT, {
            params,
        });
        return data ?? null;
    },

    async listProjectsRaw(projectId) {
        const scope = await this.resolveScope(projectId);
        const { data } = await apiClient.get(
            ENDPOINTS.HIERARCHY.PROJECTS.LIST(scope.taskForceId, scope.pillarId),
        );
        const projects = normalizeProjectListPayload(data);
        const mine = projects.filter((pr) => pr.id === scope.projectId);
        return { scope, projects: mine };
    },

    async listProjectsForManagement(projectId) {
        const { scope, projects } = await this.listProjectsRaw(projectId);
        const rows = await Promise.all(
            projects.map(async (pr) => {
                const { data: tasks } = await apiClient.get(
                    ENDPOINTS.HIERARCHY.TASKS.LIST(scope.taskForceId, scope.pillarId, pr.id),
                );
                const taskList = normalizeTaskListPayload(tasks);
                return mapProjectRowForManagement(pr, taskList);
            }),
        );
        return rows;
    },

    /** All project rows across assigned projects (for the Project Management hub). */
    async listAllProjectsForManagement() {
        const projects = await this.getMyProjects();
        const merged = [];
        for (const p of projects) {
            try {
                const rows = await this.listProjectsForManagement(p.id);
                for (const pr of rows) {
                    merged.push({
                        ...pr,
                        projectId: pr.projectId ?? p.id,
                        projectName: pr.projectName ?? p.name ?? '',
                    });
                }
            } catch {
                /* skip project */
            }
        }
        return merged;
    },

    /**
     * Flat task list for Project Management hub (optional filters).
     * @param {{ projectId?: string }} filters
     */
    async listTasksFlat(filters = {}, currentUserId) {
        const projects = await this.getMyProjects();
        const list = filters.projectId
            ? projects.filter((x) => x.id === filters.projectId)
            : projects;
        const rows = [];
        for (const p of list) {
            let scope;
            try {
                scope = await this.resolveScope(p.id);
            } catch {
                continue;
            }
            const { data: tasks } = await apiClient.get(
                ENDPOINTS.HIERARCHY.TASKS.LIST(scope.taskForceId, scope.pillarId, p.id),
            );
            const taskList = normalizeTaskListPayload(tasks);
            for (const t of taskList) {
                rows.push({
                    ...mapApiTaskToChairUi(t, currentUserId),
                    projectId: p.id,
                    projectName: p.name ?? '',
                });
            }
        }
        return rows;
    },

    async getProjectBundle(projectId, currentUserId) {
        const scope = await this.resolveScope(projectId);
        const [{ data: projectRes }, { data: tasks }] = await Promise.all([
            apiClient.get(ENDPOINTS.HIERARCHY.PROJECTS.DETAIL(scope.taskForceId, scope.pillarId, projectId)),
            apiClient.get(ENDPOINTS.HIERARCHY.TASKS.LIST(scope.taskForceId, scope.pillarId, projectId)),
        ]);
        const project = projectRes?.data ?? projectRes;
        if (!project) throw new Error('Project not found');
        const taskList = normalizeTaskListPayload(tasks);
        const mappedTasks = taskList.map((t) => mapApiTaskToChairUi(t, currentUserId));
        return {
            pillar: { id: scope.pillarId, taskForceId: scope.taskForceId },
            project: {
                id: project.id,
                name: project.name,
                description: project.description,
                startDate: project.projectStartDate ? String(project.projectStartDate).slice(0, 10) : '',
                endDate: project.projectEndDate ? String(project.projectEndDate).slice(0, 10) : '',
                tasks: mappedTasks,
            },
        };
    },

    async createTask(projectId, payload) {
        const scope = await this.resolveScope(projectId);
        const body = {
            name: String(payload.name ?? '').trim(),
            description: String(payload.description ?? '').trim(),
            startDate: payload.startDate ? toIsoDateStart(payload.startDate) : undefined,
            endDate: toIsoDateEnd(payload.endDate),
            weight: normalizeInt1To100(payload.weight, 'Weight', DEFAULT_WEIGHT),
            targetValue: normalizeInt1To100(
                payload.targetValue,
                'Target value',
                DEFAULT_TARGET_VALUE,
            ),
            ...(payload.isStar != null ? { isStar: Boolean(payload.isStar) } : {}),
            ...(payload.reportRange != null ? { reportRange: Math.trunc(Number(payload.reportRange)) } : {}),
        };
        const { data } = await apiClient.post(
            ENDPOINTS.HIERARCHY.TASKS.CREATE(scope.taskForceId, scope.pillarId, projectId),
            body,
        );
        return data;
    },

    async updateTask(projectId, taskId, payload) {
        const scope = await this.resolveScope(projectId);
        const patch = {};
        if (payload.name !== undefined) patch.name = String(payload.name ?? '').trim();
        if (payload.description !== undefined) patch.description = String(payload.description ?? '').trim();
        if (payload.startDate !== undefined) patch.startDate = toIsoDateStart(payload.startDate);
        if (payload.endDate !== undefined) patch.endDate = toIsoDateEnd(payload.endDate);
        if (payload.weight !== undefined) {
            patch.weight = normalizeInt1To100(payload.weight, 'Weight');
        }
        if (payload.targetValue !== undefined) {
            patch.targetValue = normalizeInt1To100(payload.targetValue, 'Target value');
        }
        if (payload.isStar !== undefined) patch.isStar = Boolean(payload.isStar);
        if (payload.reportRange !== undefined) patch.reportRange = Math.trunc(Number(payload.reportRange));
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.TASKS.UPDATE(scope.taskForceId, scope.pillarId, projectId, taskId),
            patch,
        );
        return data;
    },

    /* ── Task checkpoints (named stages on the progress track) ── */

    async listTaskCheckpoints(projectId, taskId) {
        const scope = await this.resolveScope(projectId);
        const { data } = await apiClient.get(
            ENDPOINTS.HIERARCHY.TASKS.CHECKPOINTS(scope.taskForceId, scope.pillarId, projectId, taskId),
        );
        return normalizeCheckpointListPayload(data);
    },

    async createTaskCheckpoint(projectId, taskId, payload) {
        const scope = await this.resolveScope(projectId);
        const { data } = await apiClient.post(
            ENDPOINTS.HIERARCHY.TASKS.CHECKPOINTS(scope.taskForceId, scope.pillarId, projectId, taskId),
            toCheckpointPayload(payload),
        );
        return data;
    },

    async updateTaskCheckpoint(projectId, taskId, checkpointId, payload) {
        const scope = await this.resolveScope(projectId);
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.TASKS.CHECKPOINT(
                scope.taskForceId,
                scope.pillarId,
                projectId,
                taskId,
                checkpointId,
            ),
            toCheckpointPayload(payload),
        );
        return data;
    },

    async deleteTaskCheckpoint(projectId, taskId, checkpointId) {
        const scope = await this.resolveScope(projectId);
        await apiClient.delete(
            ENDPOINTS.HIERARCHY.TASKS.CHECKPOINT(
                scope.taskForceId,
                scope.pillarId,
                projectId,
                taskId,
                checkpointId,
            ),
        );
        return { id: checkpointId };
    },

    async listAssignableUsers() {
        const { data } = await apiClient.get(ENDPOINTS.USERS.LIST, {
            params: { memberAssignableOnly: true },
        });
        const list = Array.isArray(data?.users) ? data.users : [];
        return list.map((u) => ({
            id: u.id,
            name: displayNameFromUser(u),
            email: u.email,
            role: primaryRoleLabel(u),
        }));
    },

    async assignUserToTask(projectId, taskId, { userId, isChair = false }) {
        const scope = await this.resolveScope(projectId);
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.TASKS.ASSIGN(scope.taskForceId, scope.pillarId, projectId, taskId),
            { userId, isChair },
        );
        return data;
    },

    async removeTaskAssignee(projectId, taskId, userId) {
        const scope = await this.resolveScope(projectId);
        await apiClient.delete(
            ENDPOINTS.HIERARCHY.TASKS.REMOVE_ASSIGNEE(scope.taskForceId, scope.pillarId, projectId, taskId, userId),
        );
        return { ok: true };
    },

    async sendTaskMessage(projectId, taskId, { senderId, message }) {
        const scope = await this.resolveScope(projectId);
        const { data } = await apiClient.post(
            ENDPOINTS.HIERARCHY.TASKS.MESSAGES(scope.taskForceId, scope.pillarId, projectId, taskId),
            { senderId, message },
        );
        return data;
    },

    async verifyInProgress(projectId, taskId, inProgressId, { note } = {}) {
        const scope = await this.resolveScope(projectId);
        // The note is mandatory on both approve and reject (backend Joi min 5),
        // so it is always sent rather than omitted when blank.
        const body = { note: String(note ?? '').trim() };
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.TASKS.INPROGRESS_VERIFY(
                scope.taskForceId,
                scope.pillarId,
                projectId,
                taskId,
                inProgressId,
            ),
            body,
        );
        return data;
    },

    async rejectInProgress(projectId, taskId, inProgressId, { note } = {}) {
        const scope = await this.resolveScope(projectId);
        // The note is mandatory on both approve and reject (backend Joi min 5),
        // so it is always sent rather than omitted when blank.
        const body = { note: String(note ?? '').trim() };
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.TASKS.INPROGRESS_REJECT(
                scope.taskForceId,
                scope.pillarId,
                projectId,
                taskId,
                inProgressId,
            ),
            body,
        );
        return data;
    },

    // ── Project Locations ──────────────────────────────────────────────────

    async listCities() {
        const { data } = await apiClient.get(ENDPOINTS.CITIES.LIST);
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.data)) return data.data;
        return [];
    },

    async listProjectLocations({ cityId } = {}) {
        const scope = await this.resolveScope();
        const { data } = await apiClient.get(
            ENDPOINTS.HIERARCHY.PROJECT_LOCATIONS.LIST(scope.taskForceId, scope.pillarId),
            { params: cityId ? { cityId: String(cityId) } : undefined },
        );
        return Array.isArray(data) ? data : [];
    },

    /** body: { cityId, name, description?, latitude?, longitude?, images? } */
    async createProjectLocation(body) {
        const scope = await this.resolveScope();
        const { data } = await apiClient.post(
            ENDPOINTS.HIERARCHY.PROJECT_LOCATIONS.CREATE(scope.taskForceId, scope.pillarId),
            {
                cityId: String(body.cityId ?? '').trim(),
                name: String(body.name ?? '').trim(),
                description: body.description != null ? String(body.description) : undefined,
                latitude: body.latitude != null ? Number(body.latitude) : undefined,
                longitude: body.longitude != null ? Number(body.longitude) : undefined,
                images: Array.isArray(body.images) ? body.images : [],
            },
        );
        return data;
    },

    /** patch: { name?, description?, latitude?, longitude?, images? } */
    async updateProjectLocation(locationId, patch) {
        const scope = await this.resolveScope();
        const body = {};
        if (patch.name !== undefined) body.name = String(patch.name ?? '').trim();
        if (patch.description !== undefined) body.description = String(patch.description ?? '');
        if (patch.latitude !== undefined) body.latitude = patch.latitude != null ? Number(patch.latitude) : null;
        if (patch.longitude !== undefined) body.longitude = patch.longitude != null ? Number(patch.longitude) : null;
        if (patch.images !== undefined) body.images = Array.isArray(patch.images) ? patch.images : [];
        const { data } = await apiClient.put(
            ENDPOINTS.HIERARCHY.PROJECT_LOCATIONS.UPDATE(scope.taskForceId, scope.pillarId, locationId),
            body,
        );
        return data;
    },

    async deleteProjectLocation(locationId) {
        const scope = await this.resolveScope();
        await apiClient.delete(
            ENDPOINTS.HIERARCHY.PROJECT_LOCATIONS.DELETE(scope.taskForceId, scope.pillarId, locationId),
        );
        return { ok: true };
    },

    /** Upload images via the evidence endpoint and return MinIO object keys. */
    async uploadLocationImages(files) {
        const formData = new FormData();
        for (const file of files) {
            formData.append('files', file);
        }
        const { data } = await apiClient.post(ENDPOINTS.STORAGE.EVIDENCE, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return Array.isArray(data?.objectKeys) ? data.objectKeys : [];
    },
};

export default projectManagerService;
